// climateGlsl.ts — Shared GLSL for the analytic per-fragment surface climate, so the
// surface, ocean, and cloud shells all derive the SAME temperature (and the frost /
// scorch fields from it) and therefore agree on where a world is frozen, temperate, or
// baked. Without this the three shells each guessed independently — the classic symptom
// being a tidally-locked world that still drew a glinting blue ocean and fluffy white
// cloud directly on its scorching substellar face.
//
// This is the interim, analytic stand-in for the precomputed PlaSim climate maps (see
// the climate plan): it captures the structure that matters most for the look — a
// tidally-locked world's scorched substellar point, temperate terminator ring, and
// frozen night face — from the day/night/mean temperatures the sidebar already shows.
// `climateTempC` here is a verbatim mirror of `surfaceTempC` in surfaceShader.ts, so the
// seabed (surface) and the water shell (ocean) and the deck (clouds) agree bit-for-bit.
// When the baked monthly maps land, climateTempC / scorch / frost become a texture
// sample and every shell upgrades together.
export const CLIMATE_PARS = /* glsl */ `
  uniform float uMeanT;     // planet mean surface temp (degC)
  uniform float uDayT;      // day-side / lit-face average (degC)
  uniform float uNightT;    // night-side / dark-face average (degC)
  uniform float uObliq;     // axial tilt 0..1 (flattens the equator-pole contrast)
  uniform float uTidalLock; // 1 = synchronous rotator (substellar temperature field)

  // Per-fragment surface temperature (degC). Rotators: annual-mean insolation falls with
  // latitude (warm equator, cold poles), flattened by axial tilt. Tidally locked: follows
  // the substellar angle dayDot — scorched substellar point, temperate terminator, frozen
  // night face. Mirrors surfaceShader.ts surfaceTempC so every shell agrees.
  float climateTempC(float latY, float dayDot) {
    if (uTidalLock > 0.5) {
      float s = clamp(dayDot, -1.0, 1.0);
      float subOver = max(40.0, (uDayT - uMeanT) * 0.7);
      float dayC = mix(uMeanT, uDayT + subOver, smoothstep(0.0, 1.0, s));
      float nightC = mix(uMeanT, uNightT, smoothstep(0.0, -1.0, s));
      return s >= 0.0 ? dayC : nightC;
    }
    float lat = abs(latY);
    float wInsol = mix(cos(lat * 1.5707963), 1.0, 0.5 * uObliq);
    float spread = clamp((uDayT - uNightT) * 0.5 + 50.0, 45.0, 100.0);
    return uMeanT + spread * (wInsol - 0.55);
  }

  // Frozen fraction (0..1): caps / sea ice grow well above freezing so a temperate world
  // gets broad caps, not just summit snow.
  float climateFrost(float tempC) { return smoothstep(8.0, -35.0, tempC); }
  // Scorched-vegetation fraction (0..1): warm + dry parches greenery to arid brown. A hot
  // rotator's equator hits this; it only recolours LAND, it does not boil oceans.
  float climateScorch(float tempC) { return smoothstep(40.0, 95.0, tempC); }
  // Boil-off fraction (0..1): only genuinely searing surfaces — a tidally-locked world's
  // substellar cap, a molten-hot dayside — where liquid water evaporates to a dry basin
  // and convection (cloud) shuts off. Threshold sits FAR above scorch so a merely-warm
  // equator (a hot rotator, ~60C) keeps its blue ocean and its clouds; only the true
  // hot face of a synchronous world bakes. This is what kills "oceans on the hot side".
  float climateBake(float tempC) { return smoothstep(80.0, 170.0, tempC); }
`;
